/**
 * Sprint 18: Breadcrumbs Component
 * 
 * Provides contextual navigation breadcrumbs for work item hierarchy pages.
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home, Layers, Package, FileText, CheckSquare } from 'lucide-react';

const BREADCRUMB_ITEMS = {
  'delivery': { label: 'Delivery', path: '/work-items', icon: Package },
  'epics': { label: 'Epics', path: '/epics', icon: Layers },
  'features': { label: 'Features', path: '/features', icon: FileText },
  'stories': { label: 'Stories', path: '/stories', icon: FileText },
  'tasks': { label: 'Tasks', path: '/tasks', icon: CheckSquare },
  'work-items': { label: 'Work Items', path: '/work-items', icon: Layers },
};

const Breadcrumbs = ({ items, className = '' }) => {
  const location = useLocation();
  
  // Auto-generate breadcrumbs from path if items not provided
  const breadcrumbs = items || generateBreadcrumbsFromPath(location.pathname);
  
  if (breadcrumbs.length === 0) return null;

  return (
    <nav className={`flex items-center text-sm ${className}`} aria-label="Breadcrumb">
      <ol className="flex items-center space-x-1">
        {breadcrumbs.map((item, index) => {
          const isLast = index === breadcrumbs.length - 1;
          const Icon = item.icon || null;
          
          return (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <ChevronRight className="w-4 h-4 text-[var(--clr-text-muted)] mx-1" />
              )}
              {isLast ? (
                <span className="flex items-center gap-1.5 text-[var(--clr-text-primary)] font-medium">
                  {Icon && <Icon className="w-4 h-4" />}
                  {item.label}
                </span>
              ) : (
                <Link 
                  to={item.path}
                  className="flex items-center gap-1.5 text-[var(--clr-text-secondary)] hover:text-[var(--clr-brand-primary)] transition-colors"
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

function generateBreadcrumbsFromPath(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs = [];
  
  // Always start with Delivery for work item pages
  const workItemPages = ['epics', 'features', 'stories', 'tasks', 'work-items'];
  
  if (segments.some(s => workItemPages.includes(s))) {
    breadcrumbs.push({
      label: 'Delivery',
      path: '/work-items',
      icon: Package
    });
    
    breadcrumbs.push({
      label: 'Work Items',
      path: '/work-items',
      icon: Layers
    });
  }
  
  // Add current page
  const lastSegment = segments[segments.length - 1];
  if (BREADCRUMB_ITEMS[lastSegment] && lastSegment !== 'work-items') {
    breadcrumbs.push({
      ...BREADCRUMB_ITEMS[lastSegment],
      icon: BREADCRUMB_ITEMS[lastSegment].icon
    });
  }
  
  return breadcrumbs;
}

// Helper to generate breadcrumbs for specific work item type
export const getWorkItemBreadcrumbs = (type) => {
  const breadcrumbs = [
    { label: 'Delivery', path: '/work-items', icon: Package },
    { label: 'Work Items', path: '/work-items', icon: Layers }
  ];
  
  if (type && BREADCRUMB_ITEMS[type.toLowerCase()]) {
    breadcrumbs.push({
      ...BREADCRUMB_ITEMS[type.toLowerCase()],
      icon: BREADCRUMB_ITEMS[type.toLowerCase()].icon
    });
  }
  
  return breadcrumbs;
};

export default Breadcrumbs;
